import React, { useState } from 'react';
import styles from './ProportionalBar.module.scss';

export interface ProportionalBarItem {
	label: string;
	value: number;
	color: string;
}

interface Props {
	items: ProportionalBarItem[];
}

const HIDDEN_DOT_COLOR = '#d1d5db';

const ProportionalBar: React.FC<Props> = ({ items }) => {
	const [hiddenIndexes, setHiddenIndexes] = useState<Set<number>>(new Set());

	const total = items.reduce((sum, item) => sum + item.value, 0);
	const visibleItems = items.filter((_, i) => !hiddenIndexes.has(i));
	const visibleTotal = visibleItems.reduce((sum, item) => sum + item.value, 0);

	const toggleItem = (index: number) => {
		const isHidden = hiddenIndexes.has(index);
		if (!isHidden && visibleItems.length <= 1) return;
		setHiddenIndexes(prev => {
			const next = new Set(prev);
			if (isHidden) {
				next.delete(index);
			} else {
				next.add(index);
			}
			return next;
		});
	};

	const getPercentage = (value: number) => total > 0 ? Math.round((value / total) * 100) : 0;

	const getVisiblePercentages = () => {
		if (visibleTotal === 0) return items.map(() => 0);
		const raw = items.map((item, i) => hiddenIndexes.has(i) ? 0 : (item.value / visibleTotal) * 100);
		const rounded = raw.map(v => Math.round(v));
		const diff = 100 - rounded.reduce((s, v) => s + v, 0);
		if (diff !== 0) {
			const maxIdx = rounded.reduce((best, v, i) => v > rounded[best] ? i : best, 0);
			rounded[maxIdx] += diff;
		}
		return rounded;
	};

	const visiblePcts = getVisiblePercentages();

	return (
		<div className={styles.container}>
			<div className={styles.bar}>
				{items.map((item, i) => {
					if (hiddenIndexes.has(i) || visiblePcts[i] === 0) return null;
					return (
						<div
							key={i}
							className={styles.barSegment}
							style={{ width: `${visiblePcts[i]}%`, backgroundColor: item.color }}
						>
							{visiblePcts[i] >= 10 && <span className={styles.barLabel}>{visiblePcts[i]}%</span>}
						</div>
					);
				})}
			</div>
			<div className={styles.legendContainer}>
				{items.map((item, i) => {
					const isHidden = hiddenIndexes.has(i);
					const pct = getPercentage(item.value);
					return (
						<div
							key={i}
							className={`${styles.legendItem} ${isHidden ? styles.legendItemHidden : ''}`}
							onClick={() => toggleItem(i)}
						>
							<div className={styles.legendHeader}>
								<span className={styles.legendDot} style={{ backgroundColor: isHidden ? HIDDEN_DOT_COLOR : item.color }} />
								<span className={styles.legendLabel}>{item.label}</span>
							</div>
							<div className={styles.legendValues}>
								<span className={styles.legendValue}>{item.value}</span>
								<span className={styles.legendPct}>{pct}%</span>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default ProportionalBar;
